import mitt from 'mitt';
import { get } from 'lodash-es';
import { createWithZone } from './time_utils';
// Validations
import { extend } from '~/utils/vee_validate_compat';
import validations from '~/vee-validations';
import { max, required } from '@vee-validate/rules';

const _recurringJobDialogEvents = mitt();

export const recurringJobsDialogEvents = {
  EVENTS: {
    OPEN: 'rjde:open',
    CLOSE: 'rjde:close',
    CLOSED: 'rjde:closed',
    LOADING: 'rjde:loading',
    CONFIRM: 'rjde:save',
  },

  open(componentName, { dialogProps, dialogEvents } = {}) {
    _recurringJobDialogEvents.emit(this.EVENTS.OPEN, {
      dialogName: componentName,
      dialogProps,
      dialogEvents,
    });
  },
  close(componentName) {
    _recurringJobDialogEvents.emit(this.EVENTS.CLOSE, componentName);
  },
  closed(componentName) {
    _recurringJobDialogEvents.emit(`${this.EVENTS.CLOSED}:${componentName}`);
  },
  setLoading(isLoading) {
    _recurringJobDialogEvents.emit(this.EVENTS.LOADING, isLoading);
  },
  confirm(componentName) {
    // Scope the event so that we can explicitly listen for it
    // rather than having to check the incoming payload in the
    // different parts of the app.
    _recurringJobDialogEvents.emit(`${this.EVENTS.CONFIRM}:${componentName}`);
  },
  on(event, callback) {
    _recurringJobDialogEvents.on(event, callback);
  },
  once(event, callback) {
    _recurringJobDialogEvents.on(event, callback);
  },
  off(event, callback) {
    _recurringJobDialogEvents.off(event, callback);
  },
};

// For debugging purposes
if (!import.meta.env.PROD) {
  window.recurringJobsDialogEvents = recurringJobsDialogEvents;
}

/**
 * Helper function to help us initialize the `vee-validation` rules
 * that we need inside the work orders feature.
 */
export function addWorkOrdersValidationRules() {
  extend('max', {
    ...max,
    // Declare the `length` param so the compat message interpolates `{length}`
    // (the v4 rule is a bare function with no params metadata — without this the
    // message renders the literal "{length}").
    params: ['length'],
    message: `{_field_} must be {length} characters or less`,
  });
  extend('required', { ...required });
  // `required_if` was removed from @vee-validate/rules v4; the compat shim
  // (app/javascript/utils/vee_validate_compat.js) registers a
  // replacement globally via defineRule('required_if', …).

  // Add VeeValidate custom rules
  extend('gte', validations.gte);
  extend('greaterThanZero', validations.greaterThanZero);
}

/**
 * Helper that is used to `mapInertiaProps` to whether or not we can
 * allow the user to manage the budget for a work order.
 *
 * @param {Object} $page.props | State that contains the `organization` and `current_user`
 * @returns Boolean
 */
export function canManageBudget({ organization, current_user: currentUser }) {
  const userCanManageBudget = get(currentUser, 'can_manage_budgeting', false);
  const orgHasBudgetFeature = get(organization, 'has_feature_budgeting', false);

  return userCanManageBudget && orgHasBudgetFeature;
}

/**
 * Helper function that is used to grouped a list of clients into
 * a list of active and archived clients; as well as preparing
 * each item for the "select" component.
 *
 * @param {Object} $page.props | State that contains the `clients`
 * @returns Array List of clients ready for the "select" component
 */
export function groupedClientsForSelect({ clients }) {
  const activeClients = clients.filter((client) => client.status === 0);
  const archivedClients = clients.filter((client) => client.status === 1);
  const getClientData = ({ id, name }) => ({ id, text: name });
  const groupedOptionsForSelect = activeClients.map(getClientData);

  if (archivedClients.length) {
    groupedOptionsForSelect.push({
      text: 'Archived',
      children: archivedClients.map(getClientData),
    });
  }

  return groupedOptionsForSelect;
}

/**
 * Helper function to sort and prepare for the select component the active
 * organization members that we have comming from the backend.
 *
 * @param {Object} $page.props | State that contains the `organization_members`
 * @returns
 */
export function activeOrganizationMembers({ organization_members: organizationMembers }) {
  return organizationMembers
    .filter((member) => member.active)
    .map(({ id, name: text }) => ({ id, text }))
    .sort((ua, ub) => ua.text.localeCompare(ub.text));
}

/**
 * Helper function that is necessary for some legacy reasons when we were dealing
 * with the attendance part of the feature.
 *
 * @param {Object} $page.props | State that contains the `organization`
 * @returns String the time zone
 */
export function realOrgTimeZone({ organization }) {
  return get(organization, '_time_zone_name', get(organization, 'time_zone_name'));
}

/**
 * Helper function to to convert time to a time with timezone.
 *
 * @param {Moment} date Date to apply as base
 * @param {Moment} time Time to use for the date
 * @param {String} timezone Tiemzone to convert time and date
 * @returns Moment
 */
export function datetimeWithTz(date, time, timezone) {
  return timezone
    ? createWithZone(date, time, timezone)
    : date.clone().set({
        hours: time.hours(),
        minutes: time.minutes(),
        seconds: time.seconds(),
      });
}
